import { formatShortMonthDayYear } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import PersonIcon from "@mui/icons-material/Person";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import { ButtonBase, Chip, CircularProgress, IconButton, Paper, Stack } from "@mui/material";
import { useUiModalState } from "@src/appV2/lib/analytics";
import { Text } from "@src/appV2/redesign/components/Text";
import pluralize from "pluralize";
import { useHistory } from "react-router-dom";

import { useWorker } from "../api/useWorker";
import { useIsAdmin } from "../../lib/useIsAdmin";
import { getHomeHealthEditVisitPath } from "../paths";
import { PricingType, type Visit, VISIT_TYPE_LABELS, VisitStatus, VisitType } from "../types";
import { MobileCancelVisitDialog } from "./MobileCancelVisitDialog";

const EDITABLE_VISIT_STATUSES = [
  VisitStatus.OPEN,
  VisitStatus.FILLED,
  VisitStatus.LOGGED,
  VisitStatus.CONFIRMED,
  VisitStatus.PENDING,
];

function statusLabel(status: VisitStatus): string {
  return status.charAt(0) + status.slice(1).toLowerCase();
}

function VisitStatusChip({ status }: { status: VisitStatus }) {
  if (status === VisitStatus.OPEN) {
    return <Chip label={statusLabel(status)} variant="outlined" size="small" />;
  }

  return (
    <Chip
      label={statusLabel(status)}
      size="small"
      sx={{
        bgcolor: (theme) => theme.palette.text.primary,
        color: (theme) => theme.palette.background.paper,
      }}
    />
  );
}

function BookedWorkerName({ workerId }: { workerId: string }) {
  const { data: worker, isLoading } = useWorker(workerId, { useErrorBoundary: false });

  if (isLoading) {
    return <CircularProgress size={14} />;
  }

  return (
    <Text variant="body1" data-dd-privacy="mask">
      {worker?.name}
    </Text>
  );
}

interface Props {
  visit: Visit;
}

export function MobileVisitCard({ visit }: Props) {
  const history = useHistory();
  const isAdmin = useIsAdmin();
  const cancelModalState = useUiModalState({
    surfaceKind: "dialog",
    surfaceName: "home_health_mobile_visit_cancel",
  });
  const { attributes } = visit;
  const {
    type,
    status,
    workerReq,
    deadline,
    visitsPerWeek,
    durationInWeeks,
    pricingType,
    estimatedDuration,
    payRate,
    chargeRate,
    bookedWorkerId,
    caseId,
  } = attributes;

  const visitTitle = [VISIT_TYPE_LABELS[type], workerReq].filter(Boolean).join(" ");
  const isVisitEditable = EDITABLE_VISIT_STATUSES.includes(status) || isAdmin;
  const isVisitCancelable = status !== VisitStatus.CANCELED;

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        borderRadius: 2,
        px: 2.5,
        py: 2,
        bgcolor: "background.paper",
        boxShadow: "0 1px 4px 0 rgba(0,0,0,0.08)",
        padding: "1rem",
      }}
    >
      {/* The whole card, padding included, is one tap target that opens the edit
          flow. An overlay (rather than wrapping the content in the button) keeps
          the detail rows at full card width and the pencil/cancel icons as
          sibling buttons; the actions stack is lifted above the overlay so those
          icons stay tappable. */}
      {isVisitEditable && (
        <ButtonBase
          focusRipple
          aria-label={`Edit ${visitTitle} visit`}
          data-dd-action-name="Edit visit card"
          sx={{ position: "absolute", inset: 0, borderRadius: 2 }}
          onClick={() => {
            history.push(getHomeHealthEditVisitPath(caseId, visit.id));
          }}
        />
      )}
      <Stack spacing={1.25}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Text variant="h6">{visitTitle}</Text>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ position: "relative", zIndex: 1 }}
          >
            <VisitStatusChip status={status} />
            {isVisitEditable && (
              <IconButton
                aria-label="Edit visit"
                size="small"
                onClick={() => {
                  history.push(getHomeHealthEditVisitPath(caseId, visit.id));
                }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            )}
            {isVisitCancelable && (
              <IconButton
                aria-label="Cancel visit"
                size="small"
                onClick={cancelModalState.openModal}
              >
                <CancelOutlinedIcon fontSize="small" color="error" />
              </IconButton>
            )}
          </Stack>
        </Stack>

        {isDefined(bookedWorkerId) && (
          <Stack direction="row" spacing={1} alignItems="center">
            <PersonIcon sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }} />
            <BookedWorkerName workerId={bookedWorkerId} />
          </Stack>
        )}

        {isDefined(deadline) && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CalendarTodayOutlinedIcon
              sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }}
            />
            <Text variant="body1">
              First visit due: {formatShortMonthDayYear(new Date(deadline))}
            </Text>
          </Stack>
        )}

        {type === VisitType.REGULAR && isDefined(visitsPerWeek) && isDefined(durationInWeeks) && (
          <Stack direction="row" spacing={1} alignItems="center">
            <RadioButtonUncheckedIcon
              sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }}
            />
            <Text variant="body1">
              {visitsPerWeek}/week for {pluralize("week", durationInWeeks, true)}
            </Text>
          </Stack>
        )}

        {type === VisitType.REGULAR &&
          pricingType === PricingType.PER_HOUR &&
          isDefined(estimatedDuration) && (
            <Stack direction="row" spacing={1} alignItems="center">
              <AccessTimeOutlinedIcon
                sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }}
              />
              <Text variant="body1">Estimated duration: {estimatedDuration} hours</Text>
            </Stack>
          )}

        {isDefined(payRate) && (
          <Stack direction="row" spacing={1} alignItems="center">
            <MonetizationOnOutlinedIcon
              sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }}
            />
            <Text variant="body1">Pay rate: ${(payRate.amountInMinorUnits / 100).toFixed(2)}</Text>
          </Stack>
        )}

        {isDefined(chargeRate) && (
          <Stack direction="row" spacing={1} alignItems="center">
            <ReceiptOutlinedIcon sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }} />
            <Text variant="body1">
              Charge rate: ${(chargeRate.amountInMinorUnits / 100).toFixed(2)}
            </Text>
          </Stack>
        )}
      </Stack>

      <MobileCancelVisitDialog visit={visit} modalState={cancelModalState} />
    </Paper>
  );
}
